import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ApiService from '../services/api';
import {
  displayPrefsFromSettingsRows,
  DEFAULT_DISPLAY_TIMEZONE,
  normalizeDateFormat,
  normalizeTimeFormat,
} from '../utils/siteDisplayDateTime';

const DisplayPreferencesContext = createContext(null);

const defaultPrefs = {
  timeZone: DEFAULT_DISPLAY_TIMEZONE,
  dateFormat: normalizeDateFormat('dmy'),
  timeFormat: normalizeTimeFormat('24h'),
};

export function DisplayPreferencesProvider({ children }) {
  const [prefs, setPrefs] = useState(defaultPrefs);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await ApiService.getSystemSettings();
      if (res?.success && Array.isArray(res.data)) {
        setPrefs(displayPrefsFromSettingsRows(res.data));
      } else {
        setPrefs(defaultPrefs);
      }
    } catch {
      setPrefs(defaultPrefs);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onRefresh = () => {
      load();
    };
    window.addEventListener('admin:display-prefs-changed', onRefresh);
    return () => window.removeEventListener('admin:display-prefs-changed', onRefresh);
  }, [load]);

  const value = useMemo(() => ({ prefs, loading, reload: load }), [prefs, loading, load]);

  return <DisplayPreferencesContext.Provider value={value}>{children}</DisplayPreferencesContext.Provider>;
}

export function useDisplayPreferences() {
  const ctx = useContext(DisplayPreferencesContext);
  if (!ctx) {
    return { prefs: defaultPrefs, loading: false, reload: async () => {} };
  }
  return ctx;
}
